"use client"

import * as React from "react"
import { AlertTriangle } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog"
import { Button } from "./button"
import { useTranslation } from "@/app/hooks/useTranslation"

export interface ConfirmationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  message?: string
  confirmText?: string
  cancelText?: string
  variant?: "default" | "destructive"
  onConfirm: () => void | Promise<void>
  onCancel?: () => void
  loading?: boolean
}

export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  message,
  confirmText,
  cancelText,
  variant = "default",
  onConfirm,
  onCancel,
  loading = false,
}: ConfirmationDialogProps) {
  const { t } = useTranslation()
  const [isProcessing, setIsProcessing] = React.useState(false)

  // Detect dark mode
  const isDarkMode = React.useMemo(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark')
    }
    return false
  }, [])

  const dialogStyles = {
    position: 'fixed' as const,
    left: '50vw',
    top: '50vh',
    transform: 'translate(-50%, -50%)',
    zIndex: 9999999999, // Increased to 10 nines - higher than AccountSwitcherDropdown's 9 nines
    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    color: isDarkMode ? '#f9fafb' : '#111827',
    borderRadius: '8px',
    padding: '24px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  }

  const titleStyles = {
    color: isDarkMode ? '#f9fafb' : '#111827',
    fontSize: '18px',
    fontWeight: '600'
  }

  const descriptionStyles = {
    color: isDarkMode ? '#d1d5db' : '#6b7280',
    fontSize: '14px',
    marginTop: '8px'
  }

  const cancelButtonStyles = {
    backgroundColor: isDarkMode ? '#374151' : '#ffffff',
    border: `1px solid ${isDarkMode ? '#6b7280' : '#d1d5db'}`,
    color: isDarkMode ? '#f9fafb' : '#374151',
    padding: '8px 16px',
    borderRadius: '6px',
    fontWeight: '500',
    transition: 'all 0.2s'
  }

  const confirmButtonStyles = variant === "destructive" 
    ? {
        backgroundColor: '#dc2626',
        color: '#ffffff',
        padding: '8px 16px',
        border: 'none',
        borderRadius: '6px',
        fontWeight: '500',
        transition: 'all 0.2s'
      }
    : {
        backgroundColor: '#3b82f6',
        color: '#ffffff',
        padding: '8px 16px',
        border: 'none',
        borderRadius: '6px',
        fontWeight: '500',
        transition: 'all 0.2s'
      }

  const handleConfirm = async () => {
    try {
      setIsProcessing(true)
      await onConfirm()
      onOpenChange(false)
    } catch (error) {
      console.error('Confirmation action failed:', error)
      // Keep dialog open on error so user can retry or cancel
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
    onOpenChange(false)
  }

  const isLoading = loading || isProcessing

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen && onCancel) {
        onCancel()
      }
      onOpenChange(newOpen)
    }}>
      <DialogContent 
        className="sm:max-w-[425px]" 
        style={dialogStyles}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            {variant === "destructive" && (
              <AlertTriangle className="h-5 w-5" style={{ color: '#dc2626' }} />
            )}
            <DialogTitle style={titleStyles}>
              {title || t('dialog.confirmation.generic.title')}
            </DialogTitle>
          </div>
          <DialogDescription className="whitespace-pre-line" style={descriptionStyles}>
            {message || t('dialog.confirmation.generic.message')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            style={cancelButtonStyles}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = isDarkMode ? '#4b5563' : '#f9fafb'
                e.currentTarget.style.borderColor = isDarkMode ? '#9ca3af' : '#9ca3af'
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#ffffff'
                e.currentTarget.style.borderColor = isDarkMode ? '#6b7280' : '#d1d5db'
              }
            }}
          >
            {cancelText || t('dialog.confirmation.generic.cancel')}
          </Button>
          <Button
            type="button"
            variant={variant === "destructive" ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={isLoading}
            style={confirmButtonStyles}
            onMouseEnter={(e) => {
              if (!isLoading) {
                if (variant === "destructive") {
                  e.currentTarget.style.backgroundColor = '#b91c1c'
                } else {
                  e.currentTarget.style.backgroundColor = '#2563eb'
                }
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                if (variant === "destructive") {
                  e.currentTarget.style.backgroundColor = '#dc2626'
                } else {
                  e.currentTarget.style.backgroundColor = '#3b82f6'
                }
              }
            }}
          >
            {isLoading ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {t('common.loading')}
              </>
            ) : (
              confirmText || t('dialog.confirmation.generic.confirm')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Hook for easier usage
export function useConfirmationDialog() {
  const [dialogState, setDialogState] = React.useState<{
    open: boolean
    props: Omit<ConfirmationDialogProps, 'open' | 'onOpenChange'>
  }>({
    open: false,
    props: {
      onConfirm: () => {},
    },
  })

  const showConfirmation = React.useCallback((props: Omit<ConfirmationDialogProps, 'open' | 'onOpenChange'>) => {
    setDialogState({
      open: true,
      props,
    })
  }, [])

  const hideConfirmation = React.useCallback(() => {
    setDialogState(prev => ({
      ...prev,
      open: false,
    }))
  }, [])

  const ConfirmationDialogComponent = React.useCallback(() => (
    <ConfirmationDialog
      {...dialogState.props}
      open={dialogState.open}
      onOpenChange={hideConfirmation}
    />
  ), [dialogState, hideConfirmation])

  return {
    showConfirmation,
    hideConfirmation,
    ConfirmationDialog: ConfirmationDialogComponent,
  }
}